// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// The PRE-FREEZE DOCUMENT REVIEW panel on the Gate tab.
//
// WHAT IT GATES, AND WHY THAT IS DIFFERENT FROM THE PANEL BELOW IT. The retail-copy
// review row on this tab is INFORMATIONAL: it gates the SEND, via
// reporting_deliveries_guard step 4. This panel gates the SIGN. Its subject is an
// internal contradiction inside the very artifact the signature binds, so it is a
// hard disable rather than a notice. Keep the two visually distinct and always say
// which act each one gates.
//
// The hard disable is legitimate here (unlike the advisory artifact-binding mirror,
// which can drift MORE restrictive than the RPC) because it is a SAFE SUPERSET: it
// reads the identical predicate the server applies at approve_reporting_gate leg
// (iii), from the identical two rows. It can never be stricter than the wall.
//
// It shows the BINDING (artifact_sha256 / ir_sha256) rather than asking the reader
// to trust it: the rows are written in the same request as the freeze, against a
// render proven byte-deterministic, so they always describe the bytes just frozen.

import { AlertTriangle, CheckCircle2, FileSearch, HelpCircle, ShieldAlert } from "lucide-react";

import type { DocumentReviewState, ReviewFindingRow } from "@/hooks/investment-reporting/useDocumentReview";

const SIGN_LABEL: Record<string, string> = {
  positive: "positive",
  negative: "negative",
  zero: "zero",
  not_found: "not resolved",
};

/** What each rule asserts, in one line, for a reader who has never seen the rule
 *  set. The rule ids are stable and stamped with a RULES_VERSION. */
const RULE_BLURB: Record<string, string> = {
  "DR-1": "the sentence asserts one direction and prints a figure of the opposite sign",
  "DR-3": "a direction claim carrying no figures of its own, contradicted by every figure in its paragraph",
  "DR-4": "a double negative: the direction word already carries the sign, so the figure reads as a magnitude",
  "DR-5": "a directional claim printed around a figure that did not resolve",
  "DR-6": "an excess printed in prose with no unit; in the tables the unit is carried by the caption",
  "DR-7": "the comparator named in the sentence is not the comparator the figure measures against",
};

function shortSha(s: string | null): string {
  return s ? `${s.slice(0, 12)}…` : "—";
}

function FindingCard({ f }: { f: ReviewFindingRow }) {
  const isBlocking = f.blocking === true;
  const label = f.rule ?? f.category ?? "finding";
  const blurb = f.rule ? RULE_BLURB[f.rule] : undefined;
  return (
    <li
      className={`rounded-md border p-3 text-sm ${
        isBlocking ? "border-destructive/50 bg-destructive/5" : "border-border bg-muted/30"
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`rounded px-1.5 py-0.5 font-mono text-[11px] font-medium ${
            isBlocking ? "bg-destructive/15 text-destructive" : "bg-muted text-muted-foreground"
          }`}
        >
          {label}
        </span>
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
          {isBlocking ? "blocks the gate" : f.severity}
        </span>
        <span className="font-mono text-[11px] text-muted-foreground">
          {f.slot}
          {typeof f.paragraph_index === "number" ? ` p${f.paragraph_index}` : ""}
          {typeof f.sentence_index === "number" ? `s${f.sentence_index}` : ""}
        </span>
        {f.source === "llm" && (
          <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[11px] text-primary">
            reviewer, advisory{f.confidence ? ` · ${f.confidence} confidence` : ""}
          </span>
        )}
      </div>

      {/* The document's own words. Every finding must quote verbatim; a quote that
          is not a substring of the slot is discarded before it ever reaches here. */}
      <blockquote className="mt-2 border-l-2 border-border pl-3 text-foreground/90">
        {f.quote}
      </blockquote>

      {Array.isArray(f.observed) && f.observed.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {f.observed.slice(0, 12).map((o, i) => (
            <span
              key={`${o.ref}-${i}`}
              title={o.ref}
              className="rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[11px]"
            >
              {o.printed}
              <span className="ml-1 text-muted-foreground">{SIGN_LABEL[o.sign] ?? o.sign}</span>
            </span>
          ))}
          {f.observed.length > 12 && (
            <span className="px-1 text-[11px] text-muted-foreground">
              and {f.observed.length - 12} more
            </span>
          )}
        </div>
      )}

      <p className="mt-2 text-[13px] text-muted-foreground">{f.message ?? f.why}</p>
      {blurb && <p className="mt-1 text-[11px] italic text-muted-foreground">{blurb}</p>}

      {Array.isArray(f.cell_refs) && f.cell_refs.length > 0 && (
        <p className="mt-1 font-mono text-[10px] text-muted-foreground">
          {f.cell_refs.slice(0, 6).join("  ")}
          {f.cell_refs.length > 6 ? `  +${f.cell_refs.length - 6}` : ""}
        </p>
      )}
    </li>
  );
}

export default function DocumentReviewPanel({ state }: { state: DocumentReviewState | undefined }) {
  if (!state) return null;

  const blocking = state.findings.filter((f) => f.blocking === true);
  const advisory = state.findings.filter((f) => f.blocking !== true);

  // An ABSENT pair is the case worth naming loudly. Gate leg (iii) counts FAILING
  // rows, so "never reviewed" and "reviewed clean" are the same thing to the SQL
  // unless document_review_ran exists to tell them apart.
  if (!state.present) {
    return (
      <section className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
        <header className="flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-amber-600" aria-hidden />
          <h3 className="font-heading text-base font-medium text-foreground">
            Document review — not run
          </h3>
        </header>
        <p className="mt-2 text-sm text-muted-foreground">
          No pre-freeze review rows exist for this cycle, so the frozen document has not been
          checked against the figures printed inside it. This is not the same as a clean review.
          Re-render the newsletter to run the review.
        </p>
      </section>
    );
  }

  const headline = !state.ran
    ? "Document review did not complete"
    : blocking.length > 0
      ? `Document review found ${blocking.length} blocking ${blocking.length === 1 ? "contradiction" : "contradictions"}`
      : "Document review passed";
  const tone = !state.ran || blocking.length > 0 ? "bad" : "good";

  return (
    <section
      className={`rounded-lg border p-4 ${
        tone === "bad" ? "border-destructive/50 bg-destructive/5" : "border-border bg-card"
      }`}
    >
      <header className="flex flex-wrap items-center gap-2">
        {tone === "bad"
          ? <ShieldAlert className="h-4 w-4 text-destructive" aria-hidden />
          : <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden />}
        <h3 className="font-heading text-base font-medium text-foreground">{headline}</h3>
        <span className="ml-auto text-[11px] uppercase tracking-wide text-muted-foreground">
          gates the signature
        </span>
      </header>

      <p className="mt-2 text-sm text-muted-foreground">
        This check reads the assembled newsletter after every figure was substituted, and asks
        whether any sentence contradicts the figures printed beside it. It judges prose against
        those figures only. It makes no assertion about whether a reported return or benchmark
        figure is itself correct.
      </p>

      {!state.ran && (
        <p className="mt-2 rounded border border-destructive/40 bg-background p-2 text-sm text-destructive">
          Reviewer status <span className="font-mono">{state.reviewerStatus ?? "unknown"}</span>.
          The review did not reach a verdict, so this document counts as unreviewed and the gate
          is held.
        </p>
      )}

      {/* The binding, shown rather than trusted. */}
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] sm:grid-cols-4">
        {([
          ["artifact", shortSha(state.artifactSha256)],
          ["IR", shortSha(state.irSha256)],
          ["verdict", shortSha(state.verdictSha256)],
          ["rules", state.rulesVersion ?? "—"],
        ] as Array<[string, string]>).map(([k, v]) => (
          <div key={k}>
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="font-mono text-foreground/80">{v}</dd>
          </div>
        ))}
      </dl>

      {state.llm && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          Reviewer leg: <span className="font-mono">{String(state.llm.status ?? "—")}</span>
          {state.llm.mode ? ` (mode ${String(state.llm.mode)})` : ""}
          {state.llm.model ? ` · ${String(state.llm.model)}` : ""}
          . Reviewer findings are advisory and never block on their own.
        </p>
      )}

      {blocking.length > 0 && (
        <div className="mt-4">
          <h4 className="flex items-center gap-1.5 text-sm font-medium text-destructive">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
            Blocking
          </h4>
          <ul className="mt-2 space-y-2">
            {blocking.map((f) => <FindingCard key={f.id} f={f} />)}
          </ul>
          <p className="mt-2 text-[12px] text-muted-foreground">
            These cannot be resolved from this tab. The prose is authored upstream, so the fix is
            to correct the sentence in the narrative and re-run the cycle from verify. This stage
            flags; it never rewrites client copy.
          </p>
        </div>
      )}

      {advisory.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-sm font-medium text-foreground">
            {advisory.length} advisory {advisory.length === 1 ? "note" : "notes"} (do not block)
          </summary>
          <ul className="mt-2 space-y-2">
            {advisory.map((f) => <FindingCard key={f.id} f={f} />)}
          </ul>
        </details>
      )}

      {state.mustExamine.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm font-medium text-foreground">
            <FileSearch className="mr-1.5 inline h-3.5 w-3.5" aria-hidden />
            {state.mustExamine.length} {state.mustExamine.length === 1 ? "sentence" : "sentences"} the
            rules declined to judge
          </summary>
          <p className="mt-1 text-[12px] text-muted-foreground">
            Abstentions, not passes. A hedged claim, a mixed-direction sentence or an ambiguous
            negation is a judgement a lexical rule should not make, so it is recorded rather than
            guessed at.
          </p>
          <ul className="mt-2 space-y-2">
            {state.mustExamine.map((m, i) => (
              <li key={i} className="rounded border border-border bg-muted/30 p-2 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                    {String(m.reason ?? "abstained")}
                  </span>
                  <span className="font-mono text-[11px] text-muted-foreground">
                    {String(m.slot ?? "")}
                  </span>
                </div>
                <blockquote className="mt-1.5 border-l-2 border-border pl-3 text-foreground/90">
                  {String(m.quote ?? "")}
                </blockquote>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
